import React, { createContext, useContext, useState } from 'react';
import { 
  RiCheckboxCircleLine, 
  RiCloseCircleLine, 
  RiCloseLine,
  RiInformationLine,
  RiAlertTriangleLine,
  RiAlertFill
} from 'react-icons/ri';
import '../styles/Toast.css';

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = (type, message, duration = 4000, action = null) => {
    const id = Date.now() + Math.random();
    const toast = { id, type, message, duration, action };
    
    setToasts(prev => {
      const newToasts = [...prev, toast];
      // Keep only the last 5 toasts (remove oldest if more than 5)
      return newToasts.length > 5 ? newToasts.slice(-5) : newToasts;
    });
    
    // Auto remove after duration
    setTimeout(() => {
      removeToast(id);
    }, duration);
    
    return id;
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  // Convenience methods
  const showSuccess = (message, duration, action) => addToast('success', message, duration, action);
  const showError = (message, duration, action) => addToast('error', message, duration, action);
  const showInfo = (message, duration, action) => addToast('info', message, duration, action);
  const showWarning = (message, duration, action) => addToast('warning', message, duration, action);

  const value = {
    toasts,
    addToast,
    removeToast,
    showSuccess,
    showError,
    showInfo,
    showWarning
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
};

const ToastContainer = ({ toasts, onRemove }) => {
  if (toasts.length === 0) return null;

  return (
    <div className="toast-container">
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} onRemove={onRemove} />
      ))}
    </div>
  );
};

const Toast = ({ toast, onRemove }) => {
  const getIcon = (type) => {
    switch (type) {
      case 'success':
        return <RiCheckboxCircleLine />;
      case 'error':
        return <RiCloseCircleLine />;
      case 'warning':
        return <RiAlertFill />;
      case 'info':
        return <RiInformationLine />;
      default:
        return <RiInformationLine />;
    }
  };

  const handleToastClick = () => {
    if (toast.action && typeof toast.action === 'function') {
      toast.action();
      onRemove(toast.id); // Remove toast after action is executed
    }
  };

  return (
    <div 
      className={`toast toast-${toast.type} ${toast.action ? 'toast-clickable' : ''}`}
      onClick={handleToastClick}
    >
      <div className="toast-content">
        <div className="toast-icon">
          {getIcon(toast.type)}
        </div>
        <span className="toast-message">{toast.message}</span>
        <button 
          className="toast-close"
          onClick={(e) => {
            e.stopPropagation(); // Prevent triggering toast click action
            onRemove(toast.id);
          }}
        >
          <RiCloseLine />
        </button>
      </div>
    </div>
  );
};